// scoring.js
// Pure helpers used across CSV export, rescore flows, PDFs, etc.
// No React, no Jotai — just data in -> data out.

export function buildDictsFromItems(items = []) {
	const original = {};
	const modified = {};
	const qSet = new Set();

	items.forEach((it) => {
		const q = Number(it.item_no);
		qSet.add(q);

		// originals
		const o = Array.isArray(it.original_answer)
			? it.original_answer
			: it.original_answer
				? [it.original_answer]
				: [""];
		original[q] = o.map((x) => String(x || "").toUpperCase());

		// If item is omitted, modified should be empty (not fallback to original)
		if (it.omit === "Y") {
			modified[q] = [""];
			return;
		}

		// effective modified: prefer modified_answer if present, else original
		const mArr = Array.isArray(it.modified_answer)
			? it.modified_answer
			: it.modified_answer
				? [it.modified_answer]
				: null;

		const eff = (mArr && mArr.length > 0 ? mArr : o).map((x) => String(x || "").toUpperCase());
		modified[q] = eff;
	});

	const sortedQs = Array.from(qSet).sort((a, b) => a - b);
	return { original, modified, sortedQs };
}

export function buildKeyMapByVersion(keys) {
	// keys can be an array or an object keyed by version; normalize to array
	const arr = Array.isArray(keys) ? keys : Object.values(keys || {});
	const byVersion = {};

	arr.forEach((k) => {
		const version = String(k.version.trim() || "single");
		const map = {};

		(k.items || []).forEach((it) => {
			const q = Number(it.item_no);

			if (it.omit === "Y") {
				map[q] = new Set(); // empty set => omitted
				return;
			}

			// Prefer modified over original
			const src =
				Array.isArray(it.modified_answer) && it.modified_answer.length > 0
					? it.modified_answer
					: Array.isArray(it.original_answer)
						? it.original_answer
						: [it.original_answer];

			const s = new Set();
			(src || []).forEach((ans) => {
				const up = String(ans || "")
					.trim()
					.toUpperCase();
				if (up) s.add(up);
			});

			map[q] = s; // may be empty if no answers
		});

		byVersion[version] = map;
	});

	return byVersion;
}

function getItemEffectiveAnswerArray(item) {
	if (!item || item.omit === "Y") return [];
	const source =
		Array.isArray(item.modified_answer) && item.modified_answer.length > 0
			? item.modified_answer
			: Array.isArray(item.original_answer)
				? item.original_answer
				: item.original_answer
					? [item.original_answer]
					: [];

	return (source || [])
		.map((answer) =>
			String(answer || "")
				.trim()
				.toUpperCase(),
		)
		.filter(Boolean);
}

function normalizePositiveWeight(rawWeight) {
	const parsed = Number.parseFloat(rawWeight);
	if (!Number.isFinite(parsed) || parsed <= 0) return 1;
	return Number(parsed.toFixed(3));
}

export function formatWeightBreakdownValue(rawWeight) {
	return String(normalizePositiveWeight(rawWeight));
}

export function normalizeSelectedQuestions(selectedQuestions, lqnum = null, hqnum = null) {
	if (!Array.isArray(selectedQuestions) && !(selectedQuestions instanceof Set)) return null;
	const selected = new Set();
	for (const raw of selectedQuestions) {
		const q = Number(raw);
		if (Number.isInteger(q) && q > 0) selected.add(q);
	}
	const lq = Number(lqnum);
	const hq = Number(hqnum);
	if (Number.isFinite(lq) && Number.isFinite(hq)) {
		for (const q of [...selected]) {
			if (q < lq || q > hq) selected.delete(q);
		}
	}
	return selected;
}

export function computeKeyMaxScore(keyObj, selectedQuestions = null) {
	const items = Array.isArray(keyObj?.items) ? keyObj.items : [];
	if (items.length === 0) return 0;
	const selectedSet = normalizeSelectedQuestions(selectedQuestions);

	let total = 0;
	for (const item of items) {
		const itemNo = Number(item?.item_no);
		if (selectedSet && Number.isFinite(itemNo) && !selectedSet.has(itemNo)) continue;
		const answers = getItemEffectiveAnswerArray(item);
		if (answers.length === 0) continue;
		const w = Number.parseFloat(item?.weight);
		total += Number.isFinite(w) && w > 0 ? w : 1;
	}
	return total;
}

export function computeExamMaxScore(answerKeys, version = null, selectedQuestions = null) {
	const keys = Array.isArray(answerKeys) ? answerKeys : Object.values(answerKeys || {});
	if (keys.length === 0) return 0;

	if (version !== null && version !== undefined) {
		const versionStr = String(version || "").trim();
		const matched = keys.find((k) => String(k?.version || "").trim() === versionStr);
		if (matched) return computeKeyMaxScore(matched, selectedQuestions);
	}

	return keys.reduce((max, key) => Math.max(max, computeKeyMaxScore(key, selectedQuestions)), 0);
}

export function getBonusMarksFromOutputSettings(outputSettings) {
	const bonus = Number(outputSettings?.bonusMarks ?? 0);
	if (!Number.isFinite(bonus) || bonus < 0) return 0;
	return bonus;
}

export function getUniqueWeightBreakdownValues(answerKeys, selectedQuestions = null) {
	const keys = Array.isArray(answerKeys) ? answerKeys : Object.values(answerKeys || {});
	const selectedSet = normalizeSelectedQuestions(selectedQuestions);
	const weights = new Set();

	for (const key of keys) {
		for (const item of key?.items || []) {
			const itemNo = Number(item?.item_no);
			if (selectedSet && Number.isFinite(itemNo) && !selectedSet.has(itemNo)) continue;
			if (getItemEffectiveAnswerArray(item).length === 0) continue;
			weights.add(formatWeightBreakdownValue(item?.weight));
		}
	}

	return [...weights].sort((left, right) => {
		const leftNumber = Number(left);
		const rightNumber = Number(right);
		if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
			return leftNumber - rightNumber;
		}
		return String(left).localeCompare(String(right));
	});
}

export function isWeightedExam(answerKeys, selectedQuestions = null) {
	const keys = Array.isArray(answerKeys) ? answerKeys : Object.values(answerKeys || {});
	const selectedSet = normalizeSelectedQuestions(selectedQuestions);

	return keys.some((key) =>
		(key?.items || []).some((item) => {
			const itemNo = Number(item?.item_no);
			if (selectedSet && Number.isFinite(itemNo) && !selectedSet.has(itemNo)) return false;
			if (getItemEffectiveAnswerArray(item).length === 0) return false;
			return Math.abs(normalizePositiveWeight(item?.weight) - 1) > 1e-9;
		}),
	);
}

function getResponseCharForQuestion(resp, questionNo, lqnum, hqnum) {
	const chars = Array.from(String(resp || ""));
	if (chars.length === 0) return "";

	const q = Number(questionNo);
	const lq = Number(lqnum);
	const hq = Number(hqnum);

	let idx;
	if (Number.isFinite(hq) && hq > 0 && chars.length >= hq) {
		idx = q - 1;
	} else if (Number.isFinite(lq)) {
		idx = q - lq;
	} else {
		idx = q - 1;
	}

	return Number.isInteger(idx) && idx >= 0 && idx < chars.length ? chars[idx] || "" : "";
}

export function gradeResponseString(resp, answerSetByQ, keyObj, header, outputSettings) {
	let score = 0;
	const wrongQs = [];
	const scoredChars = [];
	const weightBreakdown = {};
	const itemsFromDB = keyObj.items || [];
	const lqnum = Number(header.lqnum) || 0;
	const hqnum = Number(header.hqnum) || 0;
	const selectedQuestions = normalizeSelectedQuestions(
		outputSettings?.selectedQuestions,
		lqnum,
		hqnum,
	);

	//console.log("Grading response:", { resp, answerSetByQ, keyObj, header, outputSettings });

	for (let i = 0; i < hqnum - lqnum + 1; i++) {
		const q = lqnum + i;
		if (selectedQuestions && !selectedQuestions.has(q)) {
			scoredChars.push(".");
			continue;
		}

		// if (itemsFromDB.some((it) => Number(it.item_no) === q && it.omit === "Y")) {
		// 	continue;
		// }
		const set = answerSetByQ[q] || new Set(); // empty => omit

		// console.log(`Q${q}: answer set =`, answerSetByQ[35].size);

		const raw = getResponseCharForQuestion(resp, q, lqnum, hqnum);

		if (set.size === 0) {
			// omitted item
			scoredChars.push(".");
			continue;
		}
		if (!raw || raw.trim() === "") {
			// blank
			scoredChars.push("-");
			wrongQs.push(q);
			continue;
		}

		const up = raw.toUpperCase();
		const down = raw.toLowerCase();
		if (set.has(up)) {
			const itemFromDB = itemsFromDB.find((it) => Number(it.item_no) === q);
			if (!itemFromDB) {
				// shouldn't happen
				console.error("Missing item in keyObj for q=", q);
			} else {
				const weight = normalizePositiveWeight(itemFromDB.weight);
				score += weight;
				const weightKey = formatWeightBreakdownValue(weight);
				weightBreakdown[weightKey] = Number(
					((weightBreakdown[weightKey] || 0) + weight).toFixed(3),
				);
				scoredChars.push(up);
			}
		} else {
			scoredChars.push(down);
			wrongQs.push(q);
		}
	}

	if (outputSettings && outputSettings.scalingEnabled) {
		const maxScore = computeKeyMaxScore(keyObj, selectedQuestions);
		const target = Number(outputSettings.scalingTarget || 0);
		if (maxScore > 0 && Number.isFinite(target) && target > 0) {
			score = Math.round((score / maxScore) * target);
		}
	}

	const bonusMarks = getBonusMarksFromOutputSettings(outputSettings);
	score = Number((score + bonusMarks).toFixed(3));

	return {
		score,
		wrongStr: "W= " + wrongQs.join(" "),
		scoredStr: "R=" + scoredChars.join(""),
		weightBreakdown,
	};
}

// [
//     {
//         "item_no": 1,
//         "omit": "N",
//         "weight": "1.000",
//         "notes": "",
//         "answers": [
//             {
//                 "answer_code": "B",
//                 "kind": "O"
//             }
//         ],
//         "modified_answer": null,
//         "original_answer": [
//             "B"
//         ]
//     },
//     {
//         "item_no": 2,
//         "omit": "N",
//         "weight": "1.000",
//         "notes": "",
//         "answers": [
//             {
//                 "answer_code": "A",
//                 "kind": "O"
//             }
//         ],
//         "modified_answer": null,
//         "original_answer": [
//             "A"
//         ]
//     },
//     {
//         "item_no": 3,
//         "omit": "N",
//         "weight": "1.000",
//         "notes": "",
//         "answers": [
//             {
//                 "answer_code": "A",
//                 "kind": "O"
//             }
//         ],
//         "modified_answer": null,
//         "original_answer": [
//             "A"
//         ]
//     },
//     {
//         "item_no": 4,
//         "omit": "N",
//         "weight": "1.000",
//         "notes": "",
//         "answers": [
//             {
//                 "answer_code": "D",
//                 "kind": "O"
//             }
//         ],
//         "modified_answer": null,
//         "original_answer": [
//             "D"
//         ]
//     },
//     {
//         "item_no": 5,
//         "omit": "N",
//         "weight": "1.000",
//         "notes": "",
//         "answers": [
//             {
//                 "answer_code": "C",
//                 "kind": "O"
//             }
//         ],
//         "modified_answer": null,
//         "original_answer": [
//             "C"
//         ]
//     },
//     {
//         "item_no": 6,
//         "omit": "N",
//         "weight": "1.000",
//         "notes": "",
//         "answers": [
//             {
//                 "answer_code": "B",
//                 "kind": "O"
//             }
//         ],
//         "modified_answer": null,
//         "original_answer": [
//             "B"
//         ]
//     },
//     {
//         "item_no": 7,
//         "omit": "N",
//         "weight": "1.000",
//         "notes": "",
//         "answers": [
//             {
//                 "answer_code": "D",
//                 "kind": "O"
//             }
//         ],
//         "modified_answer": null,
//         "original_answer": [
//             "D"
//         ]
//     },
//     {
//         "item_no": 8,
//         "omit": "N",
//         "weight": "1.000",
//         "notes": "",
//         "answers": [
//             {
//                 "answer_code": "A",
//                 "kind": "O"
//             }
//         ],
//         "modified_answer": null,
//         "original_answer": [
//             "A"
//         ]
//     },
//     {
//         "item_no": 9,
//         "omit": "N",
//         "weight": "1.000",
//         "notes": "",
//         "answers": [
//             {
//                 "answer_code": "A",
//                 "kind": "O"
//             }
//         ],
//         "modified_answer": null,
//         "original_answer": [
//             "A"
//         ]
//     },
//     {
//         "item_no": 10,
//         "omit": "N",
//         "weight": "1.000",
//         "notes": "",
//         "answers": [
//             {
//                 "answer_code": "D",
//                 "kind": "O"
//             }
//         ],
//         "modified_answer": null,
//         "original_answer": [
//             "D"
//         ]
//     },
//     {
//         "item_no": 11,
//         "omit": "N",
//         "weight": "1.000",
//         "notes": "",
//         "answers": [
//             {
//                 "answer_code": "A",
//                 "kind": "O"
//             }
//         ],
//         "modified_answer": null,
//         "original_answer": [
//             "A"
//         ]
//     },
//     {
//         "item_no": 12,
//         "omit": "N",
//         "weight": "1.000",
//         "notes": "",
//         "answers": [
//             {
//                 "answer_code": "C",
//                 "kind": "O"
//             }
//         ],
//         "modified_answer": null,
//         "original_answer": [
//             "C"
//         ]
//     },
//     {
//         "item_no": 13,
//         "omit": "N",
//         "weight": "1.000",
//         "notes": "",
//         "answers": [
//             {
//                 "answer_code": "D",
//                 "kind": "O"
//             }
//         ],
//         "modified_answer": null,
//         "original_answer": [
//             "D"
//         ]
//     },
//     {
//         "item_no": 14,
//         "omit": "N",
//         "weight": "1.000",
//         "notes": "",
//         "answers": [
//             {
//                 "answer_code": "A",
//                 "kind": "O"
//             }
//         ],
//         "modified_answer": null,
//         "original_answer": [
//             "A"
//         ]
//     },
//     {
//         "item_no": 15,
//         "omit": "N",
//         "weight": "1.000",
//         "notes": "",
//         "answers": [
//             {
//                 "answer_code": "C",
//                 "kind": "O"
//             }
//         ],
//         "modified_answer": null,
//         "original_answer": [
//             "C"
//         ]
//     }
// ]

export const buildOriginalAnswerDict = (answerKeyItems) => {
	// answerKeyItems: array of {item_no, original_answer, modified_answer, omit, ...}
	if (!Array.isArray(answerKeyItems)) return {};
	const answer_key = [];
	answerKeyItems.forEach((it) => {
		const q = Number(it.item_no);
		if (!Number.isFinite(q) || q < 1) return; // skip bad
		// Ensure array of strings
		const arr = Array.isArray(it.original_answer) ? it.original_answer : [""];
		if (arr.length !== 1) return; // skip bad
		if (arr[0].trim().length !== 1) return; // skip bad
		answer_key[q - 1] = arr.map((x) => String(x || "").toUpperCase());
	});
	const d = {};
	for (let j = 0; j < answer_key.length; j++)
		d[j + 1] = [String(answer_key[j] ?? "").toUpperCase()];
	return d;
};

/** Helper: CSV escaping for values. */
export function escapeCsv(s) {
	const str = String(s ?? "");
	return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

/**
 * CSV has no native text typing, so spreadsheet apps like Excel may coerce
 * numeric-looking strings. Wrap the value as a simple formula to preserve
 * leading zeros when the CSV is opened.
 */
export function formatExcelTextCell(value) {
	const str = String(value ?? "");
	if (!str) return "";
	return `="${str.replace(/"/g, '""')}"`;
}

export const parseModifiedKeyStringToDict = (s) => {
	let hasAlternate = false;
	const parts = s.split("|").map((v) => (v.trim() === " " ? "" : v.trim()));

	const dict = parts.reduce((acc, ans, i) => {
		if (ans === "") {
			acc[i + 1] = [""];
			return acc;
		} // omission
		const tokens = ans
			.split(",")
			.map((a) => a.trim().toUpperCase())
			.filter(Boolean);

		if (tokens.length > 1) hasAlternate = true;
		acc[i + 1] = tokens.length ? tokens : [""];
		return acc;
	}, {});

	return { dict, hasAlternate };
};

export function toInt(x) {
	const n = Number(x);
	return Number.isFinite(n) ? n : NaN;
}

/** Helper: make CSV payload string. */
export function toCsv(headers, rows) {
	const head = headers.join(",");
	const body = rows.map((r) => headers.map((h) => escapeCsv(r[h])).join(",")).join("\n");
	return `${head}\n${body}`;
}

/** Sanitize a single answer input against the allowed set. */
export function sanitizeSingle(raw, allowedSet) {
	const up = raw.toUpperCase().replace(/\s+/g, "");
	// First allowed letter wins; commas are ignored
	for (const ch of up) {
		if (allowedSet.has(ch)) return ch;
	}
	return "";
}

/** Sanitize a list of alternate answers against the allowed set. */
export function sanitizeAlternates(raw, allowedSet) {
	const up = raw.toUpperCase().replace(/\s+/g, "");
	const endsWithComma = up.endsWith(",");

	// Keep only A–Z and commas, then split
	const tokens = up.replace(/[^A-Z,]/g, "").split(",");

	const out = [];
	const seen = new Set();

	tokens.forEach((tok, idx) => {
		if (!tok) return; // skip empties here (we'll add trailing '' later if needed)
		// If someone typed "ABD", split into A,B,D
		for (const ch of tok) {
			if (allowedSet.has(ch) && !seen.has(ch)) {
				out.push(ch);
				seen.add(ch);
			}
		}
	});

	// Preserve UX: keep a trailing comma visible -> store empty last segment
	const partsForState = endsWithComma ? [...out, ""] : out.length ? out : [""];
	const valueForDisplay = partsForState.join(","); // "A,B," if last is ""

	return { partsForState, valueForDisplay };
}
